import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { createWithdrawalRequest } from '../services/transactionService'
import { formatCurrency } from '@/lib/utils'

const withdrawSchema = z.object({
  amount: z.number().min(5000, 'Mínimo $5,000'),
  paymentMethod: z.string().min(1, 'Selecciona un método'),
  accountDetails: z.string().min(10, 'Ingresa los datos de tu cuenta'),
})

type WithdrawFormData = z.infer<typeof withdrawSchema>

const WITHDRAW_METHODS = [
  { id: 'mercadopago', name: 'Mercado Pago', placeholder: 'Email o alias de MP' },
  { id: 'transferencia', name: 'Transferencia Bancaria', placeholder: 'CBU o alias bancario' },
  { id: 'crypto', name: 'Criptomonedas (USDT)', placeholder: 'Dirección de wallet (TRC20)' },
]

interface WithdrawModalProps {
  isOpen: boolean
  onClose: () => void
}

export function WithdrawModal({ isOpen, onClose }: WithdrawModalProps) {
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<WithdrawFormData>({
    resolver: zodResolver(withdrawSchema),
  })

  const paymentMethod = watch('paymentMethod')
  const amount = watch('amount') || 0
  const currentMethod = WITHDRAW_METHODS.find((m) => m.id === paymentMethod)

  const maxWithdraw = user?.balance || 0

  const onSubmit = async (data: WithdrawFormData) => {
    if (!user) return

    if (data.amount > maxWithdraw) {
      setError('No tienes suficiente saldo')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await createWithdrawalRequest(
        user.uid,
        data.amount,
        data.paymentMethod,
        data.accountDetails
      )

      if (result.success) {
        setSuccess(true)
      } else {
        setError(result.error || 'Error al procesar el retiro')
      }
    } catch {
      setError('Error al procesar el retiro')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    setError(null)
    setSuccess(false)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="bg-casino-black-light border-casino-gold/30 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-2xl text-casino-gold text-center">
            Retirar
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-xl text-casino-gold mb-2">¡Solicitud Enviada!</h3>
            <p className="text-gray-400 mb-6">
              Tu retiro está siendo procesado. Te notificaremos cuando se complete.
            </p>
            <Button variant="gold" onClick={handleClose}>
              Entendido
            </Button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
            {/* Available balance */}
            <div className="bg-casino-black/50 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-400">Saldo disponible</p>
              <p className="text-2xl font-bold text-casino-gold">
                {formatCurrency(maxWithdraw)}
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-red-900/30 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-2">
              <Label htmlFor="amount" className="text-casino-gold/80">
                Monto a retirar
              </Label>
              <Input
                id="amount"
                type="number"
                placeholder="10000"
                {...register('amount', { valueAsNumber: true })}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
              {errors.amount && (
                <p className="text-red-400 text-sm">{errors.amount.message}</p>
              )}
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('amount', Math.floor(maxWithdraw * 0.25))}
                  className="flex-1 text-xs"
                >
                  25%
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('amount', Math.floor(maxWithdraw * 0.5))}
                  className="flex-1 text-xs"
                >
                  50%
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('amount', Math.floor(maxWithdraw * 0.75))}
                  className="flex-1 text-xs"
                >
                  75%
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('amount', maxWithdraw)}
                  className="flex-1 text-xs"
                >
                  TODO
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-casino-gold/80">Método de Retiro</Label>
              <Select onValueChange={(value) => setValue('paymentMethod', value)}>
                <SelectTrigger className="bg-casino-black border-casino-gold/30">
                  <SelectValue placeholder="Selecciona un método" />
                </SelectTrigger>
                <SelectContent>
                  {WITHDRAW_METHODS.map((method) => (
                    <SelectItem key={method.id} value={method.id}>
                      {method.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.paymentMethod && (
                <p className="text-red-400 text-sm">{errors.paymentMethod.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountDetails" className="text-casino-gold/80">
                Datos de la cuenta
              </Label>
              <Input
                id="accountDetails"
                type="text"
                placeholder={currentMethod?.placeholder || 'Ingresa los datos de tu cuenta'}
                {...register('accountDetails')}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
              {errors.accountDetails && (
                <p className="text-red-400 text-sm">{errors.accountDetails.message}</p>
              )}
            </div>

            {amount > maxWithdraw && (
              <div className="flex items-center gap-2 text-yellow-500 text-sm">
                <AlertTriangle className="h-4 w-4" />
                <span>El monto supera tu saldo disponible</span>
              </div>
            )}

            <Button
              type="submit"
              variant="gold"
              className="w-full"
              disabled={isLoading || amount > maxWithdraw || amount <= 0}
            >
              {isLoading ? 'Procesando...' : 'SOLICITAR RETIRO'}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Los retiros son procesados en 24-48 horas hábiles
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
